import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, Inject, ElementRef } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { SpinnerService } from '../../services/spinner.service';
import { ApiEnqueteDownloadFile, ApiCreateEnqueteDownloadFilesRequest } from '../../api-client-module';
import { EnqueteDownloadFile } from '../../firestore-models/enquete-download-file';
import { QuestSet } from '../../firestore-models/quest-set';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import { NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular-material-components/moment-adapter';
import * as moment from 'moment';
import 'moment/locale/ja';

export interface EnqueteDialogData {
  change_type: number;
  enqueteDownloadFile?: ApiEnqueteDownloadFile;
}

@Component({
  selector: 'app-enquete-dialog',
  templateUrl: './enquete-dialog.component.html',
  styleUrls: ['./enquete-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ]
})
export class EnqueteDialogComponent implements OnInit {

  //アンケートダウンロードファイル情報
  enqueteDownloadFile?: ApiEnqueteDownloadFile;
  //プロジェクトID
  projectId: string;

  isAdded: boolean = false;

  //フォーム
  enqueteFormGroup!: FormGroup;
  memoControl!: FormControl;
  questSetIdControl!: FormControl;
  startDateControl!: FormControl;
  endDateControl!: FormControl;

  private enqueteDownloadFilesCollection!: AngularFirestoreCollection<EnqueteDownloadFile>;
  enqueteDownloadFiles!: Observable<EnqueteDownloadFile[]>;

  private questSetCollection!: AngularFirestoreCollection<QuestSet>;
  questSets!: Observable<QuestSet[]>;
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  currentLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<EnqueteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EnqueteDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.enqueteDownloadFilesCollection = this.angularFirestore.collection<EnqueteDownloadFile>(
      AppConst.FIRESTORE_ENQUETE_DOWNLOAD_FILES, ref => ref.where('project_id', '==', this.projectId));
    this.enqueteDownloadFiles = this.enqueteDownloadFilesCollection.valueChanges({ idField: 'id' });
    this.currentLang = this.translate.currentLang;
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.translate.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });

    this.enqueteDownloadFile = this.data.enqueteDownloadFile;
    if (!this.enqueteDownloadFile) {
      this.enqueteDownloadFile = {
        memo: '',
        quest_set_id: '',
        start_date: null as any,
        end_date: null as any,
        project_id: this.projectId,
        enabled: true
      };
      this.isAdded = true;
    }

    this.questSetCollection = this.angularFirestore.collection<QuestSet>(
      AppConst.FIRESTORE_QUEST_SET, ref => ref.where('project_id', '==', this.projectId).orderBy('created', 'desc'));
    this.questSets = this.questSetCollection.valueChanges({ idField: 'id' });

    this.createForm();
    this.memoControl = this.enqueteFormGroup.get('memo') as FormControl;
    this.questSetIdControl = this.enqueteFormGroup.get('questSetId') as FormControl;
    this.startDateControl = this.enqueteFormGroup.get('startDate') as FormControl;
    this.endDateControl = this.enqueteFormGroup.get('endDate') as FormControl;
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToEndDate() {
    return this.translate.instant('作成開始日より前の日は設定不可');
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.enqueteFormGroup = this.formBuilder.group(
      {
        memo: [this.enqueteDownloadFile!.memo],
        questSetId: [this.enqueteDownloadFile!.quest_set_id, [Validators.required]],
        startDate: [this.enqueteDownloadFile!.start_date, [Validators.required]],
        endDate: [this.enqueteDownloadFile!.end_date, [Validators.required]],
      }, {
      validator: this.isValidDate
    }
    );
  }


  /**
   * Datepickerのロケールを設定するプライベートメソッド
   * @param lang 設定したい言語コード (例: 'en', 'ja')
   */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en' : 'ja';
    
    // moment.jsのグローバルロケールを設定
    moment.locale(locale);
    
    // DateAdapterのロケールを設定
    this.dateAdapter.setLocale(locale);
    
    // 現在の言語を保存
    this.currentLang = lang;
  }

  //更新ボタン
  create(): void {
    if (this.enqueteFormGroup.valid) {
      this.enqueteDownloadFile!.memo = this.memoControl.value;
      this.enqueteDownloadFile!.quest_set_id = this.questSetIdControl.value;
      this.enqueteDownloadFile!.start_date = this.startDateControl.value ? this.startDateControl.value : null;
      this.enqueteDownloadFile!.end_date = this.endDateControl.value ? this.endDateControl.value : null;
      if (this.enqueteDownloadFile) {
        let request: ApiCreateEnqueteDownloadFilesRequest = {
          access_token: '',
          change_type: this.data.change_type,
          enquete_download_files: [this.enqueteDownloadFile]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.createEnqueteDownloadFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('作成を依頼しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'));
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('作成の依頼に失敗しました。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('作成の依頼に失敗しました。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // 作成期間整合制チェック
  isValidDate(ac: AbstractControl) {
    const startDate = ac.get('startDate');
    const endDate = ac.get('endDate');
    if (!!startDate && !!endDate && !!startDate.value && !!endDate.value) {
      if (startDate.value > endDate.value) {
        endDate.setErrors({ before_start_date: true });
      } else {
        endDate.setErrors(null);
      }
    }
  }

}
